import React, { useState } from 'react';
import { Modal, ModalHeader, ModalBody, Spinner } from 'reactstrap';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { mdiEyeOutline, mdiCloudDownloadOutline } from '@mdi/js';
import Icon from '@mdi/react';
import { downloadBlobType } from '../../../store/apis/commonService';
import { FileUtil } from '../../../utils';
import DisplayModal from '../DisplayModal/DisplayModal';
import DefaultFileImage from '../../../assets/images/FileType/defaultFile.svg';

export default function GeneralAttachmentTableModal({
  attachmentData,
  isModalOpen,
  toggle,
  type,
  hrCoCd,
}) {
  const [displayModalOpen, setDisplayModalOpen] = useState(false);
  const [displayModalFile, setDisplayModalFile] = useState(null);
  const [displayIndex, setDisplayIndex] = useState(null);
  const [downloadIndex, setDownloadIndex] = useState(null);

  const downloadFileMutation = useMutation({
    mutationFn: payload => downloadBlobType(payload),
  });

  const handleDownload = async (docName, index) => {
    if (!docName) {
      toast.error('Document data is incomplete:', docName, {
        className: 'toast-error',
      });
      return;
    }
    try {
      setDownloadIndex(index);
      const blob = await downloadFileMutation.mutateAsync({
        fileName: docName,
        type,
        hrCoCd: hrCoCd || null,
      });
      FileUtil.downloadBlobFile(blob, docName || 'document.pdf');
    } catch (error) {
      toast.error('Failed to download document', {
        className: 'toast-error',
      });
    } finally {
      setDownloadIndex(null);
    }
  };

  const handleDisplayModal = async (docName, index) => {
    if (!docName) {
      toast.error('Document data is incomplete:', docName, {
        className: 'toast-error',
      });
      return;
    }
    try {
      setDisplayIndex(index);
      const blob = await downloadFileMutation.mutateAsync({
        fileName: docName,
        type,
        hrCoCd: hrCoCd || null,
      });
      if (!blob) {
        toast.error('Failed to fetch the document for display.', {
          className: 'toast-error',
        });
        return;
      }
      const file = new File([blob], docName || 'document.pdf', {
        type: blob?.type,
      });
      setDisplayModalFile(file);
      setDisplayModalOpen(true);
    } catch (error) {
      toast.error('Failed to display document', {
        className: 'toast-error',
      });
    } finally {
      setDisplayIndex(null);
    }
  };

  return (
    <div>
      <Modal isOpen={isModalOpen} toggle={toggle} size="lg" centered>
        <ModalHeader toggle={toggle}>Attachments</ModalHeader>
        <ModalBody>
          <div>
            {attachmentData &&
              attachmentData.length > 0 &&
              attachmentData.map((doc, index) => (
                <div
                  className="d-flex flex-row align-items-center justify-content-between py-0"
                  style={{
                    borderBottom: '1px solid #EAECF0',
                    padding: '10px 0',
                  }}
                  key={doc}
                >
                  <div className="d-flex flex-row align-items-center gap-2">
                    <img
                      src={DefaultFileImage}
                      alt="file icon"
                      width="45px"
                      height="45px"
                      style={{
                        padding: '0',
                      }}
                    />
                    <span
                      style={{
                        fontWeight: 500,
                        whiteSpace: 'normal',
                        overflowWrap: 'break-word',
                        wordBreak: 'break-word',
                      }}
                    >
                      {doc}
                    </span>
                  </div>
                  <div className="d-flex flex-row justify-content-end gap-2 ms-1">
                    <button
                      type="button"
                      className="btn btn-link non-btn"
                      onClick={() => handleDisplayModal(doc, index)}
                    >
                      {downloadFileMutation.isPending &&
                      index === displayIndex ? (
                        <Spinner size="sm" color="primary" />
                      ) : (
                        <Icon path={mdiEyeOutline} size={0.8} color="#017EBD" />
                      )}
                    </button>
                    <button
                      type="button"
                      className="btn btn-link non-btn"
                      onClick={() => handleDownload(doc, index)}
                    >
                      {downloadFileMutation.isPending &&
                      index === downloadIndex ? (
                        <Spinner size="sm" color="primary" />
                      ) : (
                        <Icon
                          path={mdiCloudDownloadOutline}
                          size={0.8}
                          color="#017EBD"
                        />
                      )}
                    </button>
                  </div>
                </div>
              ))}
            {attachmentData && attachmentData.length === 0 && (
              <div
                className="d-flex flex-row align-items-center justify-content-center"
                style={{ height: '100px' }}
              >
                <span
                  style={{
                    fontWeight: '500',
                    color: '#98A2B3',
                    fontSize: '16px',
                  }}
                >
                  No documents available
                </span>
              </div>
            )}
          </div>
        </ModalBody>
      </Modal>
      {displayModalOpen && (
        <DisplayModal
          isModalOpen={displayModalOpen}
          toggle={() => setDisplayModalOpen(false)}
          title="View Attachments"
          file={displayModalFile}
          onClose={() => setDisplayModalOpen(false)}
          onButtonClick={() => setDisplayModalOpen(false)}
          buttonText="Close"
        />
      )}
    </div>
  );
}
